// State Tax Rules — Australia, FY 2025-26
//
// **B130 Wave 1 (Day 56):** renamed from `land-tax-rules.ts`. Currently scoped
// to land tax only (the original `LAND_TAX_RULES` content). Subsequent Waves
// add stamp duty (Wave 3 — GAP-WEB-SOURCE per audit), FHB / foreign / vacancy
// (Wave 4 — GAP-WEB-SOURCE), trust surcharges (Wave 4 — moves SA discretionary
// trust surcharge from inline TaxPage:1459 to here).
//
// Last reviewed: February 2026 (PwC Australian Stamp Duty & Land Tax Maps,
// 1 Feb 2026).
// Review annually by 1 July each year — state thresholds and rates change
// each FY.
// States most likely to change: VIC (COVID levy ends 2033), QLD, SA, ACT
// (fixed charge).
// Update procedure: replace threshold and bracket values only. Do not change
// the data shape.
//
// **B130 schema** is added alongside as `STATE_RULES_2025_26: TaxRule[]` —
// the existing `LAND_TAX_RULES` export is the BACKWARDS-COMPATIBILITY SHIM
// that `TaxPage.tsx` reads today. Wave 6 swaps consumers onto the loader
// (`getTaxRule`) and deletes the shim.

export interface LandTaxBracket {
  from: number;
  to: number;   // Infinity for top bracket
  rate: number; // marginal rate as decimal
  base: number; // fixed $ amount for bracket start
}

export interface LandTaxRule {
  state: string;
  taxingDate?: string;        // Displayed in reference table (e.g. "31 Dec", "30 Jun")
  individualThreshold: number;
  trustThreshold: number;
  companyThreshold: number;
  brackets: LandTaxBracket[];
  entryCharge?: number;       // Flat fee added when landValue > threshold (NSW $100)
  fixedCharge?: number;       // Per-property annual fixed charge (ACT $1,326)
  notes?: string;
}

export const LAND_TAX_RULES: Record<string, LandTaxRule> = {
  NSW: {
    state: "NSW",
    taxingDate: "31 Dec",
    individualThreshold: 1_075_000,
    trustThreshold: 0,
    companyThreshold: 0,
    entryCharge: 100, // NSW charges $100 flat once over threshold
    brackets: [
      { from: 0, to: 6_571_000, rate: 0.016, base: 0 },
      { from: 6_571_000, to: Infinity, rate: 0.02, base: 88_036 },
    ],
    notes:
      "NSW threshold frozen at $1,075,000 from 2025 (no longer indexed annually). Trusts have no threshold — taxed from the first dollar. Premium rate of 2% applies above $6,571,000.",
  },
  VIC: {
    state: "VIC",
    taxingDate: "31 Dec",
    individualThreshold: 50_000,
    trustThreshold: 25_000,
    companyThreshold: 50_000,
    brackets: [
      { from: 50_000, to: 120_000, rate: 0, base: 500 },           // flat $500
      { from: 120_000, to: 350_000, rate: 0.005, base: 975 },
      { from: 350_000, to: 550_000, rate: 0.008, base: 2_225 },
      { from: 550_000, to: 1_000_000, rate: 0.013, base: 3_825 },
      { from: 1_000_000, to: 1_800_000, rate: 0.017, base: 9_700 },
      { from: 1_800_000, to: 3_000_000, rate: 0.019, base: 23_200 },
      { from: 3_000_000, to: 5_000_000, rate: 0.0215, base: 46_000 },
      { from: 5_000_000, to: Infinity, rate: 0.0255, base: 89_000 },
    ],
    notes:
      "Victoria's COVID-19 debt levy (2024–2033) adds a flat $975 plus 0.1%–0.25% to the standard land tax. VIC has the lowest general threshold of any mainland state ($50,000 — trust threshold $25,000). Vacant residential properties may attract additional Vacant Residential Land Tax (VRLT) of 1%–3%.",
  },
  QLD: {
    state: "QLD",
    taxingDate: "30 Jun",
    individualThreshold: 734_000,
    trustThreshold: 350_000,
    companyThreshold: 350_000,
    brackets: [
      { from: 0, to: 5_000_000, rate: 0.01, base: 500 },
      { from: 5_000_000, to: 10_000_000, rate: 0.0165, base: 43_160 },
      { from: 10_000_000, to: Infinity, rate: 0.0225, base: 125_660 },
    ],
    notes:
      "QLD threshold is $734,000 for individuals ($350,000 for trusts/companies). The proposed interstate aggregation rule was rescinded — QLD assesses only QLD land.",
  },
  SA: {
    state: "SA",
    taxingDate: "30 Jun",
    individualThreshold: 505_000,
    trustThreshold: 25_000,
    companyThreshold: 505_000,
    brackets: [
      { from: 505_000, to: 800_000, rate: 0.005, base: 0 },
      { from: 800_000, to: 1_200_000, rate: 0.01, base: 1_475 },
      { from: 1_200_000, to: 2_400_000, rate: 0.02, base: 5_475 },
      { from: 2_400_000, to: Infinity, rate: 0.024, base: 29_475 },
    ],
    notes:
      "SA has aggregated all land (including trust and company holdings) since 2020. Discretionary trusts face a 0.5% surcharge on top of standard rates — the harshest trust treatment in Australia. (TODO Wave 4: extract SA trust surcharge from inline TaxPage.tsx:1459 into this rule's metadata or a sibling `trust_loss` rule.)",
  },
  WA: {
    state: "WA",
    taxingDate: "30 Jun",
    individualThreshold: 300_000,
    trustThreshold: 300_000,
    companyThreshold: 300_000,
    brackets: [
      { from: 300_000, to: 420_000, rate: 0.0025, base: 0 },
      { from: 420_000, to: 1_000_000, rate: 0.0196, base: 300 },
      { from: 1_000_000, to: 1_800_000, rate: 0.0243, base: 11_668 },
      { from: 1_800_000, to: 5_000_000, rate: 0.0268, base: 31_108 },
      { from: 5_000_000, to: 11_000_000, rate: 0.026, base: 116_868 },
      { from: 11_000_000, to: Infinity, rate: 0.0267, base: 272_868 },
    ],
    notes:
      "WA threshold is $300,000. Perth metropolitan properties also attract Metropolitan Region Improvement Tax (MRIT) of an additional 0.14% — add this to the estimated rate for metro properties.",
  },
  TAS: {
    state: "TAS",
    taxingDate: "1 Jul",
    individualThreshold: 87_000,
    trustThreshold: 87_000,
    companyThreshold: 87_000,
    brackets: [
      { from: 87_000, to: 150_000, rate: 0, base: 50 },     // flat $50
      { from: 150_000, to: 350_000, rate: 0.0045, base: 50 },
      { from: 350_000, to: 725_000, rate: 0.006, base: 950 },
      { from: 725_000, to: 1_500_000, rate: 0.01, base: 3_200 },
      { from: 1_500_000, to: Infinity, rate: 0.015, base: 10_950 },
    ],
    notes:
      "TAS has a low threshold of $87,000 — most investment properties will be liable.",
  },
  ACT: {
    state: "ACT",
    taxingDate: "Quarterly",
    individualThreshold: 0,
    trustThreshold: 0,
    companyThreshold: 0,
    fixedCharge: 1_326, // Per property per year
    brackets: [
      { from: 0, to: 150_000, rate: 0.004, base: 0 },
      { from: 150_000, to: 275_000, rate: 0.0055, base: 600 },
      { from: 275_000, to: 450_000, rate: 0.007, base: 1_287.5 },
      { from: 450_000, to: 725_000, rate: 0.0085, base: 2_512.5 },
      { from: 725_000, to: Infinity, rate: 0.01032, base: 4_850 },
    ],
    notes:
      "ACT has no tax-free threshold for investment properties. A fixed charge of $1,326 per property applies plus a progressive rate of 0.4%–1.032% on land value. Each property is assessed individually — land is NOT aggregated across parcels in the ACT.",
  },
  NT: {
    state: "NT",
    taxingDate: "—",
    individualThreshold: Infinity,
    trustThreshold: Infinity,
    companyThreshold: Infinity,
    brackets: [],
    notes: "NT does not levy land tax — the only Australian jurisdiction with no land tax.",
  },
};

// ACT land tax is assessed per parcel (not aggregated). Apply the progressive
// bracket rate to each property's land value individually, sum, then add the
// per-property fixed charge.
export function estimateACTLandTax(
  landValues: number[],
  rule: LandTaxRule,
): { bracketTotal: number; fixedChargesTotal: number } {
  const fixedCharge = rule.fixedCharge ?? 0;
  let bracketTotal = 0;
  for (const value of landValues) {
    if (value <= 0) continue;
    let tax = 0;
    for (const bracket of rule.brackets) {
      if (bracket.to === Infinity) {
        if (value > bracket.from) {
          tax += (value - bracket.from) * bracket.rate;
        }
        break;
      }
      if (value > bracket.from) {
        const upper = Math.min(bracket.to, value) - bracket.from;
        if (upper > 0) tax += upper * bracket.rate;
      }
    }
    bracketTotal += tax;
  }
  return {
    bracketTotal: Math.round(bracketTotal),
    fixedChargesTotal: Math.round(landValues.filter((v) => v > 0).length * fixedCharge),
  };
}

// ─── B130 Wave 1: schema-conformant rules array + registry call ──────────────

import { registerFY } from "./tax-rules-loader";
import type { Jurisdiction, TaxRule } from "./tax-rules-types";

/**
 * State land tax for FY 2025-26 in the canonical B130 Wave 0 schema.
 * One BracketRule per jurisdiction. Numeric values mirror `LAND_TAX_RULES`
 * above verbatim — Wave 1 zero-behaviour-change invariant.
 *
 * Per-state metadata (`taxing_date`, `individual_threshold`, `trust_threshold`,
 * `company_threshold`, `entry_charge`, `fixed_charge`, `notes`) lives on the
 * rule's `metadata` field. Wave 6 helpers decode; Wave 1 callers continue
 * reading the legacy `LAND_TAX_RULES` shim above.
 */
export const STATE_RULES_2025_26: TaxRule[] = (Object.entries(LAND_TAX_RULES) as Array<[string, LandTaxRule]>).map(
  ([code, rule]) => ({
    rule_id: `${code}/land_tax/2025-26`,
    fy: "2025-26" as const,
    category: "land_tax" as const,
    jurisdiction: code as Jurisdiction,
    shape: "brackets" as const,
    plain_english:
      `${code} land tax — annual tax on the unimproved land value of ` +
      `investment properties. Threshold $${rule.individualThreshold === Infinity ? "n/a (NT — no land tax)" : rule.individualThreshold.toLocaleString()} ` +
      `for individuals.`,
    technical:
      `${code} Land Tax Act. Brackets per ${rule.taxingDate ?? "—"} taxing date. ` +
      `Trust threshold $${rule.trustThreshold === Infinity ? "n/a" : rule.trustThreshold.toLocaleString()}; ` +
      `Company threshold $${rule.companyThreshold === Infinity ? "n/a" : rule.companyThreshold.toLocaleString()}.` +
      (rule.entryCharge ? ` Entry charge $${rule.entryCharge}.` : "") +
      (rule.fixedCharge ? ` Per-property fixed charge $${rule.fixedCharge}.` : ""),
    legislation_ref: `${code} Land Tax Act`,
    source_url: `https://www.${code.toLowerCase()}revenue.gov.au/`, // placeholder per state — Wave 4 enriches with exact pages
    source_retrieved: "2026-02-01",
    last_reviewed: "2026-05-04",
    applies_to: ["individuals", "trusts", "companies"],
    formula_ref: "TaxPage land-tax helpers (calculations.ts derivatives)",
    metadata: {
      taxing_date: rule.taxingDate,
      individual_threshold: rule.individualThreshold,
      trust_threshold: rule.trustThreshold,
      company_threshold: rule.companyThreshold,
      entry_charge: rule.entryCharge,
      fixed_charge: rule.fixedCharge,
      notes: rule.notes,
    },
    brackets: rule.brackets.map((b) => ({
      lower: b.from,
      upper: b.to === Infinity ? null : b.to,
      lower_inclusive: false,
      upper_inclusive: true,
      rate: b.rate,
      fixed_at_lower: b.base,
    })),
  }),
);

// Federal rules are registered separately by `tax-rules-2025-26.ts`. The
// loader's `registerFY` merges by `rule_id`, so this call adds the state
// rules to the FY 2025-26 registry without clobbering federal entries.
registerFY("2025-26", STATE_RULES_2025_26);
